import { useState } from "react";
import { ChevronRight } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  CONTACT_GROUPS,
  type ContactGroup,
  type WorkspaceFilter,
} from "@/lib/support-workspace";
import { supportRosterLabel } from "./supportRosterLabel";
import { cn } from "@/lib/utils";

export function WorkspaceRail({
  currentUserId,
  group,
  filter,
  counts,
  roster,
  onGroup,
  onFilter,
  onCompose,
  children,
}: {
  currentUserId?: string;
  group: ContactGroup;
  filter: WorkspaceFilter;
  counts: Record<string, number>;
  roster: { id: string; name: string | null; email: string | null }[];
  onGroup: (group: ContactGroup) => void;
  onFilter: (filter: WorkspaceFilter) => void;
  onCompose?: () => void;
  children?: React.ReactNode;
}) {
  const { t } = useLanguage();
  const [more, setMore] = useState(false);
  const row = (
    key: string,
    text: string,
    active: boolean,
    action: () => void,
    initial?: string
  ) => (
    <button
      key={key}
      type="button"
      className={cn("support-rail-row", active && "is-active")}
      onClick={action}
      aria-current={active ? "page" : undefined}
    >
      {initial && (
        <span
          className={cn(
            "support-team-avatar",
            key === `person:${currentUserId}` && "is-me"
          )}
        >
          {initial}
        </span>
      )}
      <span className="truncate">{text}</span>
      <span className="support-count">{counts[key] || ""}</span>
    </button>
  );
  return (
    <aside
      className="support-rail"
      aria-label={t("support.rail.navigation", "Support navigation")}
    >
      {onCompose && (
        <button
          type="button"
          className="support-primary self-start"
          onClick={onCompose}
        >
          {t("support.rail.newMessage", "New message")}
        </button>
      )}
      <section>
        <h2 className="support-eyebrow">
          {t("support.rail.inboxes", "Inboxes")}
        </h2>
        {CONTACT_GROUPS.map((g) =>
          row(
            g.key,
            t(`support.contact.${g.key}`, g.en),
            group === g.key && filter === "all",
            () => onGroup(g.key)
          )
        )}
      </section>
      <section>
        <h2 className="support-eyebrow">
          {t("support.rail.assigned", "Assigned")}
        </h2>
        {roster.map((person) => {
          const name = supportRosterLabel(person, t);
          return row(
            `person:${person.id}`,
            name,
            filter === `person:${person.id}`,
            () => onFilter(`person:${person.id}`),
            name.charAt(0)
          );
        })}
      </section>
      <section>
        <button
          type="button"
          className="support-eyebrow flex items-center gap-1"
          aria-expanded={more}
          onClick={() => setMore(!more)}
        >
          {t("support.rail.more", "More")}
          <ChevronRight size={12} className={more ? "rotate-90" : ""} />
        </button>
        {more && (
          <>
            {row(
              "activity",
              t("support.activity.title", "Team activity"),
              filter === "activity",
              () => onFilter("activity")
            )}
            {row(
              "overdue",
              t("support.rail.overdue", "Open over 24h"),
              filter === "overdue",
              () => onFilter("overdue")
            )}
            {row(
              "snoozed",
              t("support.rail.snoozed", "Snoozed"),
              filter === "snoozed",
              () => onFilter("snoozed")
            )}
            {children}
          </>
        )}
      </section>
    </aside>
  );
}
